import { Table, TableHead, TableRow, TableCell, Stack } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { useLedgerAccounts } from '@/hooks/useAccounts';
import { formatOrderDate } from '@liminal/utils/date';
import { AccountIdLink } from '@/components/ui/AccountIdLink';
import { MonoCell } from '@/components/ui/MonoCell';
import type { LedgerAccount } from '@/api/types';

const PAGE_SIZE = 50;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SORT_ACCESSORS = {
  accountId: (account: LedgerAccount) => account.accountId,
  transactionCount: (account: LedgerAccount) => account.transactionCount,
  firstEntryTime: (account: LedgerAccount) => account.firstEntryTime,
  lastEntryTime: (account: LedgerAccount) => account.lastEntryTime,
};

const COLUMN_COUNT = 5;

export function AccountsPage() {
  const pagination = useCursorPagination(PAGE_SIZE);
  const { data, isLoading, isError } = useLedgerAccounts({
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken,
  });
  const nextPageToken = data?.nextPageToken ?? '';

  // Seeded on Last Activity descending because that is the order the ledger
  // already returns, so the header states the truth before anything is clicked.
  const { rows: accounts, sort } = useTableSort(
    data?.accounts ?? [],
    SORT_ACCESSORS,
    'lastEntryTime',
    'desc',
  );

  return (
    <Stack spacing={3}>
      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell column="accountId" sort={sort}>
                Account
              </SortableTableCell>
              <TableCell>Kind</TableCell>
              <SortableTableCell column="transactionCount" sort={sort}>
                Transactions
              </SortableTableCell>
              <SortableTableCell column="firstEntryTime" sort={sort}>
                First Activity
              </SortableTableCell>
              <SortableTableCell column="lastEntryTime" sort={sort}>
                Last Activity
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMN_COUNT}
            isLoading={isLoading}
            isError={isError}
            isEmpty={accounts.length === 0}
            loadingRows={8}
            emptyMessage="No ledger accounts."
            errorMessage="Could not load accounts."
          >
            {accounts.map((account) => (
              <TableRow key={account.accountId} hover>
                <MonoCell>
                  <AccountIdLink accountId={account.accountId} full />
                </MonoCell>
                <TableCell>
                  {/* Named accounts are system-owned; a UUID's owner (user,
                      recipient, linked account) is resolved on its detail page
                      rather than with a lookup per row here. */}
                  {account.accountId && !UUID_PATTERN.test(account.accountId) ? (
                    <StatusPill label="System" tone="info" size="sm" />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{account.transactionCount ?? '—'}</TableCell>
                <TableCell>
                  {account.firstEntryTime ? formatOrderDate(account.firstEntryTime) : '—'}
                </TableCell>
                <TableCell>
                  {account.lastEntryTime ? formatOrderDate(account.lastEntryTime) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </DataTableBody>
        </Table>
      </Card>

      {/* No "of N": the ledger returns a cursor and no total. */}
      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={accounts.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />
    </Stack>
  );
}
